import { useQuery } from "@apollo/client";
import EditIcon from "@mui/icons-material/EditOutlined";
import LaunchIcon from "@mui/icons-material/Launch";
import {
  Avatar,
  Box,
  Button,
  Card,
  Chip,
  CircularProgress,
  Divider,
  Link,
  Typography,
} from "@mui/material";
import React from "react";
import { Link as RouterLink, useParams } from "react-router-dom";

import { ADMIN_EVENT_INSIGHT } from "../gql/queries/eventsCalendar";
import { ZONE_COLOURS } from "../helpers/const";
import { Spacing } from "../types/enum";
import { useGetEventAdminQuery, type ZoneLabel } from "../types/graphql";

const HOST_HEAT: Record<string, { bg: string; tx: string }> = {
  "Big Sister": { bg: "#5FAE8D", tx: "#0f2e20" },
  Coach: { bg: "#7FA8E0", tx: "#16315e" },
  CXT: { bg: "#C9BBE6", tx: "#3b2c5c" },
  Member: { bg: "#F4C77E", tx: "#7a430a" },
};

const APP_URL = import.meta.env.VITE_APP_URL;

/** 'SMALL_TALK' / 'small_talk' → 'Small Talk'. */
const pretty = (v?: string | null): string =>
  (v ?? "")
    .toLowerCase()
    .split(/[_\s]+/)
    .filter(Boolean)
    .map((w) => w[0].toUpperCase() + w.slice(1))
    .join(" ");

const fmtDateUTC = (d?: string | null): string =>
  d
    ? new Date(d).toLocaleDateString("en-GB", {
        weekday: "short",
        day: "numeric",
        month: "short",
        year: "numeric",
        timeZone: "UTC",
      })
    : "—";
const fmtTimeUTC = (d?: string | null): string =>
  d
    ? new Date(d).toLocaleTimeString("en-GB", {
        hour: "2-digit",
        minute: "2-digit",
        timeZone: "UTC",
      })
    : "";

interface InsightLabel {
  key: string;
  name: string;
  family: string;
}
interface Insight {
  zone: ZoneLabel;
  hostType: string;
  level: string;
  visibility: string;
  labelSource: string;
  registered: number;
  maxParticipants?: number | null;
  unlimited: boolean;
  isFull: boolean;
  labels: InsightLabel[];
}

/** A labelled key → value row. */
const Fact: React.FC<{ label: string; children: React.ReactNode }> = ({ label, children }) => (
  <Box
    sx={{ display: "flex", gap: 1.5, py: 0.75, borderBottom: "1px solid", borderColor: "divider" }}
  >
    <Typography variant="caption" color="textSecondary" sx={{ minWidth: 128, pt: 0.25 }}>
      {label}
    </Typography>
    <Box sx={{ flex: 1, fontSize: 14 }}>{children || "—"}</Box>
  </Box>
);

const EventDetailsPage: React.FC = () => {
  const { eventId } = useParams<Record<string, string>>();

  const { data, loading } = useGetEventAdminQuery({
    fetchPolicy: "network-only",
    variables: { id: eventId ?? "" },
  });
  const { data: insightData } = useQuery(ADMIN_EVENT_INSIGHT, {
    variables: { eventId },
    fetchPolicy: "cache-and-network",
    skip: !eventId,
  });

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" p={Spacing.xl}>
        <CircularProgress />
      </Box>
    );
  }

  const event = data?.getEventAdmin;
  const insight: Insight | undefined = insightData?.adminEventInsight;
  if (!event) {
    return <Typography color="error">Event not found</Typography>;
  }

  const heat = insight ? (HOST_HEAT[insight.hostType] ?? HOST_HEAT.Member) : undefined;
  const capacity = insight
    ? insight.unlimited
      ? "Unlimited"
      : `${insight.registered}/${insight.maxParticipants} signed up${insight.isFull ? " · Full" : ""}`
    : null;

  return (
    <Box display="grid" gridTemplateColumns={{ xs: "1fr", md: "1.6fr 1fr" }} gap={Spacing.m}>
      {/* Left: overview */}
      <Box display="flex" flexDirection="column" gap={Spacing.m}>
        <Card>
          {event.coverUrl && (
            <Box
              sx={{
                height: 150,
                backgroundImage: `url(${event.coverUrl})`,
                backgroundSize: "cover",
                backgroundPosition: "center",
              }}
            />
          )}
          <Box p={Spacing.l}>
            <Box display="flex" alignItems="flex-start" gap={1} flexWrap="wrap" mb={1}>
              <Typography variant="h4" sx={{ fontWeight: 600, flex: 1, minWidth: 200 }}>
                {event.title}
                {event.recurring && (
                  <Box
                    component="span"
                    title="Repeats weekly"
                    sx={{ ml: 1, color: "text.secondary" }}
                  >
                    ↺
                  </Box>
                )}
              </Typography>
              {event.isDraft && <Chip size="small" color="warning" label="Draft" />}
            </Box>

            {/* Classification chips */}
            <Box display="flex" gap={0.75} flexWrap="wrap" mb={Spacing.m}>
              {insight && (
                <Chip
                  size="small"
                  label={insight.zone}
                  sx={{ bgcolor: ZONE_COLOURS.get(insight.zone), color: "#fff", fontWeight: 600 }}
                />
              )}
              {insight && heat && (
                <Chip
                  size="small"
                  label={insight.hostType}
                  sx={{ bgcolor: heat.bg, color: heat.tx, fontWeight: 600 }}
                />
              )}
              <Chip size="small" variant="outlined" label={pretty(event.kind)} />
              {insight && <Chip size="small" variant="outlined" label={insight.level} />}
              {insight && <Chip size="small" variant="outlined" label={insight.visibility} />}
              {capacity && (
                <Chip
                  size="small"
                  label={capacity}
                  color={insight?.isFull ? "error" : "default"}
                  variant={insight?.isFull ? "filled" : "outlined"}
                />
              )}
            </Box>

            {/* Description */}
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              Description
            </Typography>
            <Typography variant="body2" sx={{ whiteSpace: "pre-wrap", mb: Spacing.m }}>
              {event.description || "No description."}
            </Typography>

            {/* Labels — same topic/skill labels the Member Events board reads */}
            <Typography variant="caption" color="textSecondary" display="block" gutterBottom>
              Reads as {insight?.labelSource ? `(${insight.labelSource})` : ""}
            </Typography>
            <Box display="flex" gap={0.75} flexWrap="wrap">
              {insight && insight.labels.length > 0 ? (
                insight.labels.map((l) => (
                  <Chip
                    key={l.key}
                    size="small"
                    label={l.name}
                    color={l.family === "skill" ? "secondary" : "primary"}
                    variant="outlined"
                  />
                ))
              ) : (
                <Typography variant="body2" color="textSecondary">
                  No topic labels — this event matches by colour zone.
                </Typography>
              )}
            </Box>

            <Divider sx={{ my: Spacing.m }} />
            <Box display="flex" gap={1} flexWrap="wrap">
              <Button
                size="small"
                variant="contained"
                startIcon={<EditIcon />}
                component={RouterLink}
                to={`/event/${event.id}/edit`}
              >
                Edit event
              </Button>
              <Button
                size="small"
                variant="outlined"
                component={RouterLink}
                to={`/event/${event.id}/invite`}
              >
                Share / invite members
              </Button>
              {APP_URL && (
                <Button
                  size="small"
                  variant="outlined"
                  endIcon={<LaunchIcon />}
                  href={`${APP_URL}/event/${event.id}`}
                  target="_blank"
                  rel="noopener noreferrer"
                >
                  Open in app
                </Button>
              )}
            </Box>
          </Box>
        </Card>
      </Box>

      {/* Right: key facts */}
      <Card sx={{ alignSelf: "start" }}>
        <Box p={Spacing.l}>
          <Typography variant="subtitle1" fontWeight={600} gutterBottom>
            Details
          </Typography>

          <Fact label="Starts">
            {fmtDateUTC(event.startDate)}
            {event.startDate && (
              <Box component="span" sx={{ color: "text.secondary", ml: 1 }}>
                {fmtTimeUTC(event.startDate)} UTC
              </Box>
            )}
          </Fact>
          <Fact label="Ends">
            {event.endDate
              ? `${fmtDateUTC(event.endDate)} · ${fmtTimeUTC(event.endDate)} UTC`
              : "—"}
          </Fact>
          <Fact label="Host">
            {event.host ? (
              <Box display="inline-flex" alignItems="center" gap={1}>
                <Avatar src={event.host.avatarUrl ?? undefined} sx={{ width: 22, height: 22 }} />
                <Link component={RouterLink} to={`/users/${event.host.id}`} color="inherit">
                  {event.host.name}
                </Link>
                {event.host.badge && (
                  <Chip
                    size="small"
                    variant="outlined"
                    label={pretty(event.host.badge)}
                    sx={{ height: 18 }}
                  />
                )}
              </Box>
            ) : (
              "—"
            )}
          </Fact>
          {event.organizer && event.organizer.id !== event.host?.id && (
            <Fact label="Organizer">
              <Link component={RouterLink} to={`/users/${event.organizer.id}`} color="inherit">
                {event.organizer.name}
              </Link>
            </Fact>
          )}
          <Fact label="Topic">{event.topic?.title}</Fact>
          <Fact label="Category">{event.eventCategory ? pretty(event.eventCategory) : "—"}</Fact>
          <Fact label="English skills">
            {event.englishSkills?.length ? (
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {event.englishSkills.map((s) => (
                  <Chip
                    key={s}
                    size="small"
                    variant="outlined"
                    label={pretty(s)}
                    sx={{ height: 20 }}
                  />
                ))}
              </Box>
            ) : (
              "—"
            )}
          </Fact>
          <Fact label="Interests">
            {event.interests?.length ? (
              <Box display="flex" gap={0.5} flexWrap="wrap">
                {event.interests.map((i) => (
                  <Chip
                    key={i}
                    size="small"
                    variant="outlined"
                    label={pretty(i)}
                    sx={{ height: 20 }}
                  />
                ))}
              </Box>
            ) : (
              "—"
            )}
          </Fact>
          <Fact label="Location">{event.location}</Fact>
          {event.meta?.joinURL && (
            <Fact label="Zoom join">
              <Link href={event.meta.joinURL} target="_blank" rel="noopener noreferrer">
                Join link ↗
              </Link>
            </Fact>
          )}
          {event.videoUrl && (
            <Fact label="Recording">
              <Link href={event.videoUrl} target="_blank" rel="noopener noreferrer">
                Video ↗
              </Link>
            </Fact>
          )}
          {event.recurring && <Fact label="Series">{event.seriesId ?? "recurring"}</Fact>}
          <Fact label="Created">{fmtDateUTC(event.createdAt)}</Fact>
          <Fact label="Updated">{fmtDateUTC(event.updatedAt)}</Fact>
          <Fact label="Event ID">
            <Typography variant="caption" sx={{ fontFamily: "monospace", wordBreak: "break-all" }}>
              {event.id}
            </Typography>
          </Fact>
        </Box>
      </Card>
    </Box>
  );
};

export default EventDetailsPage;
